/**
 * Scaling Up Holdings — manage holdings + companies + memberships.
 * Holding-owner role assigns memberships to other users.
 */
import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Plus, Building2, Users, ArrowLeft, Info } from "lucide-react";

type Role = "holding_owner" | "board_member" | "executive" | "team_member";

interface Holding {
  id: string;
  name: string;
  slug: string;
  description: string | null;
}
interface Company {
  id: string;
  holdingId: string;
  name: string;
  slug: string;
  sector: string | null;
}
interface Membership {
  id: string;
  userId: string;
  companyId: string | null;
  role: Role;
  department: string | null;
}

function ScalingUpHoldingsInner() {
  const qc = useQueryClient();
  const [showCreateHolding, setShowCreateHolding] = useState(false);
  const [selectedHoldingId, setSelectedHoldingId] = useState<string | null>(null);

  const holdingsQuery = useQuery<{ items: Holding[] }>({ queryKey: ["/api/scaling-up/holdings"] });
  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", selectedHoldingId ?? "", "companies"],
    enabled: selectedHoldingId !== null,
  });
  const membershipsQuery = useQuery<{ items: Membership[] }>({
    queryKey: ["/api/scaling-up/holdings", selectedHoldingId ?? "", "memberships"],
    enabled: selectedHoldingId !== null,
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const memberships = membershipsQuery.data?.items ?? [];

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <div className="mb-4">
        <Link href="/admin/scaling-up">
          <Button variant="ghost" size="sm" className="text-white/60 hover:text-white -ml-3">
            <ArrowLeft className="w-4 h-4 mr-1" /> Wróć do cockpit-u
          </Button>
        </Link>
      </div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Scaling Up</div>
          <h1 className="text-2xl lg:text-3xl font-extrabold">Holdings &amp; Companies</h1>
          <p className="text-white/50 text-sm mt-1 max-w-2xl">
            Holding to nadrzędny tenant. Wewnątrz dodajesz spółki, a do każdej przypisujesz członków
            (board / executive / team).
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreateHolding((v) => !v)}>
          <Plus className="w-4 h-4 mr-2" /> Nowy holding
        </Button>
      </header>

      {holdings.length === 0 && !holdingsQuery.isLoading && !showCreateHolding && (
        <Card className="mb-4 border-emerald-500/25 bg-emerald-500/[0.04]">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
            <div className="text-sm text-white/75">
              Brak holdingów. Kliknij <strong>Nowy holding</strong> u góry, żeby utworzyć pierwszy.
              Po utworzeniu pojawi się on z lewej, klik wybierze go i odsłoni panele <em>Spółki</em>{" "}
              i <em>Membership</em>.
            </div>
          </CardContent>
        </Card>
      )}

      {holdings.length > 0 && !selectedHoldingId && (
        <div className="mb-4 text-white/45 text-xs">
          ← Wybierz holding z listy po lewej, żeby zarządzać spółkami i membership.
        </div>
      )}

      {showCreateHolding && (
        <CreateHoldingForm
          onCreated={() => {
            setShowCreateHolding(false);
            qc.invalidateQueries({ queryKey: ["/api/scaling-up/holdings"] });
          }}
          onCancel={() => setShowCreateHolding(false)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-white/10 bg-white/[0.02] lg:col-span-1">
          <CardContent className="p-4">
            <div className="text-white/60 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Holdingi
            </div>
            {holdingsQuery.isLoading && (
              <ul className="space-y-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  <li key={i} className="px-3 py-2 rounded border border-white/10">
                    <Skeleton className="h-4 w-2/3 mb-1.5" />
                    <Skeleton className="h-2.5 w-24" />
                  </li>
                ))}
              </ul>
            )}
            {holdings.length === 0 && !holdingsQuery.isLoading && (
              <div className="text-white/40 text-sm">Brak holdingów.</div>
            )}
            <ul className="space-y-1">
              {holdings.map((h) => (
                <li key={h.id}>
                  <button
                    className={`w-full text-left px-3 py-2 rounded text-sm border ${selectedHoldingId === h.id ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 hover:border-white/30"}`}
                    onClick={() => setSelectedHoldingId(h.id)}
                  >
                    <div className="font-semibold">{h.name}</div>
                    <div className="text-[10px] text-white/40">{h.slug}</div>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {selectedHoldingId && (
          <>
            <Card className="border-white/10 bg-white/[0.02] lg:col-span-1">
              <CardContent className="p-4">
                <div className="text-white/60 text-xs uppercase tracking-wider mb-3">Spółki</div>
                {companiesQuery.isLoading ? (
                  <ul className="space-y-1">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <li key={i} className="border border-white/10 rounded px-3 py-1.5">
                        <Skeleton className="h-4 w-2/3 mb-1.5" />
                        <Skeleton className="h-2.5 w-20" />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <CompaniesList holdingId={selectedHoldingId} companies={companies} />
                )}
              </CardContent>
            </Card>
            <Card className="border-white/10 bg-white/[0.02] lg:col-span-1">
              <CardContent className="p-4">
                <div className="text-white/60 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Membership (rada zarządcza)
                </div>
                {membershipsQuery.isLoading ? (
                  <ul className="space-y-1">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <li key={i} className="border border-white/10 rounded px-3 py-1.5">
                        <div className="flex justify-between gap-2">
                          <Skeleton className="h-3.5 w-20" />
                          <Skeleton className="h-4 w-24 rounded-full" />
                        </div>
                        <Skeleton className="h-2.5 w-28 mt-1.5" />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <MembershipsList
                    holdingId={selectedHoldingId}
                    memberships={memberships}
                    companies={companies}
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function CreateHoldingForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/scaling-up/holdings", { name, slug, description }),
    onSuccess: onCreated,
  });

  return (
    <Card className="mb-4 border-white/10 bg-white/[0.04]">
      <CardContent className="p-4 space-y-3">
        <input
          className="w-full bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm"
          placeholder="Nazwa holdingu (np. Viral Partners Holding)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="w-full bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm font-mono"
          placeholder="slug (lowercase, np. viral-partners)"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
        />
        <textarea
          className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-20"
          placeholder="Opis (opcjonalnie)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => create.mutate()}
            disabled={!name || !slug || create.isPending}
          >
            {create.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Utwórz
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>
            Anuluj
          </Button>
          {create.isError && (
            <span className="text-red-300 text-sm">
              {create.error instanceof Error ? create.error.message : "Błąd"}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CompaniesList({ holdingId, companies }: { holdingId: string; companies: Company[] }) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [sector, setSector] = useState("");

  const create = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/scaling-up/holdings/${holdingId}/companies`, { name, slug, sector }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/scaling-up/holdings", holdingId, "companies"] });
      setShowCreate(false);
      setName("");
      setSlug("");
      setSector("");
    },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] text-white/45">{companies.length} spółek</span>
        {!showCreate && (
          <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
            <Plus className="w-3 h-3 mr-1" /> Dodaj spółkę
          </Button>
        )}
      </div>
      {showCreate && (
        <div className="space-y-2 mb-3 p-2 bg-white/[0.03] border border-white/10 rounded">
          <input
            className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs"
            placeholder="Nazwa (np. Viral.Partners SaaS)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs font-mono"
            placeholder="slug (lowercase, np. vp-saas)"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
          />
          <input
            className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs"
            placeholder="sektor (opcjonalnie, np. SaaS / Agency)"
            value={sector}
            onChange={(e) => setSector(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => create.mutate()}
              disabled={!name || !slug || create.isPending}
            >
              {create.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Utwórz
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowCreate(false)}>
              Anuluj
            </Button>
            {create.isError && (
              <span className="text-red-300 text-xs self-center">
                {create.error instanceof Error ? create.error.message : "Błąd"}
              </span>
            )}
          </div>
        </div>
      )}
      {companies.length === 0 ? (
        <div className="text-white/40 text-xs italic">
          Brak spółek w tym holdingu — kliknij „Dodaj spółkę" żeby utworzyć pierwszą.
        </div>
      ) : (
        <ul className="space-y-1">
          {companies.map((c) => (
            <li key={c.id} className="text-sm border border-white/10 rounded px-3 py-1.5">
              <div className="font-semibold">{c.name}</div>
              <div className="text-[10px] text-white/40">
                {c.slug}
                {c.sector && ` · ${c.sector}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MembershipsList({
  holdingId,
  memberships,
  companies,
}: {
  holdingId: string;
  memberships: Membership[];
  companies: Company[];
}) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [role, setRole] = useState<Role>("board_member");
  const [department, setDepartment] = useState("");

  const upsert = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/scaling-up/holdings/${holdingId}/memberships`, {
        userId,
        companyId: companyId || null,
        role,
        department: department || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/scaling-up/holdings", holdingId, "memberships"] });
      setUserId("");
      setCompanyId("");
      setRole("board_member");
      setDepartment("");
    },
  });

  return (
    <div className="space-y-3">
      <ul className="space-y-1">
        {memberships.map((m) => {
          const company = companies.find((c) => c.id === m.companyId);
          return (
            <li key={m.id} className="text-sm border border-white/10 rounded px-3 py-1.5">
              <div className="flex justify-between gap-2">
                <span className="font-mono text-xs">{m.userId.slice(0, 8)}…</span>
                <Badge className="border bg-white/5 border-white/10 text-[10px]">{m.role}</Badge>
              </div>
              <div className="text-[10px] text-white/40">
                {company ? company.name : "holding-level"}
                {m.department && ` · ${m.department}`}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="border-t border-white/10 pt-3 space-y-2">
        <input
          className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs font-mono"
          placeholder="user UUID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
        <select
          className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
        >
          <option value="">— holding-level —</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
        >
          <option value="holding_owner">holding_owner</option>
          <option value="board_member">board_member</option>
          <option value="executive">executive</option>
          <option value="team_member">team_member</option>
        </select>
        <input
          className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs"
          placeholder="dział (opcjonalnie)"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
        />
        <Button size="sm" onClick={() => upsert.mutate()} disabled={!userId || upsert.isPending}>
          {upsert.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Dodaj/zaktualizuj
        </Button>
      </div>
    </div>
  );
}

export default function ScalingUpHoldingsPage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpHoldingsInner />
    </AdminGuard>
  );
}
